import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	DeleteIcon,
	FlexGap,
	Heading,
	IconButton,
	Popover,
	PropertyChip,
	Tabs,
	TargetIcon,
	tokens,
} from '@treams/treams-ui';

import { OrganizationGoalsList } from '../OrganizationGoals';
import { ViewerGoalsList } from '../ViewerGoals';
import { CollectiveGoalsList } from '../collective-goals/Common';
import GoalSelectorV2 from './GoalSelectorV2';
import { GoalSelectorVisibility } from './GoalSelectorVisibility';

const Container = styled(Box)`
	background: ${tokens.surface.secondary};
	overflow-y: auto;
`;

const Footer = styled(Flex)`
	background: ${tokens.surface.white};
	border-top: 1px solid ${tokens.border.subtle};
`;

function GoalSelectorModal(props: {
	personalGoals?: boolean;
	goalTargetIsUser?: boolean;
	onDismiss: () => void;
	onSelect: (goal: graphql.Goal, shareGoal: boolean) => void;
}) {
	const { t } = useTranslation();
	const [goal, setGoal] = useState<graphql.Goal | undefined>(undefined);
	const [step, setStep] = useState<'GOAL' | 'VISIBILITY'>('GOAL');
	const [tab, setTab] = useState(props.personalGoals === false ? 2 : 0);

	if (step === 'VISIBILITY') {
		return (
			<GoalSelectorVisibility
				onSelect={(shareGoal) => props.onSelect(goal!, shareGoal)}
				onBack={() => setStep('GOAL')}
			/>
		);
	}

	return (
		<Flex width={600} flexDirection="column" flexShrink={1} flexGrow={1}>
			<Box flexGrow={0} flexShrink={0}>
				<Box p={3} pb={0}>
					<Heading>{t('goals.selector.header.title')}</Heading>
				</Box>

				<Tabs>
					{props.personalGoals !== false && (
						<Tabs.Item
							selected={tab === 0}
							title={t('goals.tab.personal.title')}
							onClick={() => setTab(0)}
						/>
					)}
					<Tabs.Item
						selected={tab === 1}
						title={t('goals.tab.collective.title')}
						onClick={() => setTab(1)}
					/>
					<Tabs.Item
						selected={tab === 2}
						title={t('goals.tab.organization.title')}
						onClick={() => setTab(2)}
					/>
				</Tabs>
			</Box>
			<Container p={3} flexGrow={1} flexShrink={1}>
				{tab === 0 && <ViewerGoalsList hideArchived selectedGoal={goal} onSelect={setGoal} />}
				{tab === 1 && <CollectiveGoalsList hideArchived selectedGoal={goal} onSelect={setGoal} />}
				{tab === 2 && <OrganizationGoalsList hideArchived selectedGoal={goal} onSelect={setGoal} />}
			</Container>
			<Footer
				p={3}
				width={600}
				flexGrow={0}
				flexShrink={0}
				alignItems="center"
				justifyContent="flex-end"
			>
				<Button
					type="secondary"
					title={t('goals.new.template.footer.cancel')}
					flexGrow={0}
					flexShrink={0}
					onClick={() => props.onDismiss()}
				/>
				<Button
					type="primary"
					title={t('goals.new.template.footer.continue')}
					flexGrow={0}
					flexShrink={0}
					ml={2}
					onClick={() => {
						if (!props.goalTargetIsUser) return props.onSelect(goal!, true);

						setStep('VISIBILITY');
					}}
					disabled={!goal}
				/>
			</Footer>
		</Flex>
	);
}

export default function GoalSelector(props: {
	personalGoals?: boolean;
	goalTargetIsUser?: boolean;
	goal?: graphql.Goal;
	onChange: (goal?: graphql.Goal, visibility?: graphql.GoalVisibility) => void;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	const hasParentDepartmentGoals = useFeature(graphql.FeatureName.GoalsParentDepartmentGoals);

	if (hasParentDepartmentGoals) {
		return <GoalSelectorV2 {...props} />;
	}

	return (
		<FlexGap alignItems="center" gap={tokens.gap.element}>
			<Popover.Target
				contents={
					<GoalSelectorModal
						personalGoals={props.personalGoals}
						goalTargetIsUser={props.goalTargetIsUser}
						onDismiss={() => setVisible(false)}
						onSelect={(goal, shareGoal) => {
							const visibility = shareGoal
								? graphql.GoalVisibility.Public
								: graphql.GoalVisibility.Private;
							props.onChange(goal, visibility);
							setVisible(false);
						}}
					/>
				}
				visible={visible}
				center
				onDismiss={() => setVisible(false)}
			>
				<PropertyChip
					icon={<TargetIcon />}
					label={props.goal ? localize(props.goal.title).text : t('goals.selector.select')}
					onClick={() => setVisible(true)}
				/>
			</Popover.Target>
			{props.goal && (
				<IconButton
					type="destructive"
					icon={<DeleteIcon />}
					onClick={() => props.onChange(undefined)}
				/>
			)}
		</FlexGap>
	);
}
